/**
 * Pressure and temperature units shared by the physical-chemistry calculators.
 *
 * Pressure factors are pascals per unit. The atmosphere (101 325 Pa) and the bar
 * (100 000 Pa) are exact; the conventional millimetre of mercury is exactly
 * 13 595.1 kg/m³ × 9.806 65 m/s² × 1 mm = 133.322 387 415 Pa; the torr is defined as
 * 1/760 of an atmosphere; the pound-force per square inch follows from
 * 4.448 221 615 260 5 N ÷ 0.000 645 16 m² (both exact).
 */

import { ATMOSPHERE, BAR, CELSIUS_OFFSET } from "./constants";

export interface PressureUnit {
  id: string;
  symbol: string;
  /** Pascals in one of this unit. */
  pascals: number;
}

export const PRESSURE_UNITS: readonly PressureUnit[] = [
  { id: "Pa", symbol: "Pa", pascals: 1 },
  { id: "kPa", symbol: "kPa", pascals: 1000 },
  { id: "bar", symbol: "bar", pascals: BAR },
  { id: "atm", symbol: "atm", pascals: ATMOSPHERE },
  { id: "mmHg", symbol: "mmHg", pascals: 133.322_387_415 },
  { id: "torr", symbol: "Torr", pascals: ATMOSPHERE / 760 },
  { id: "psi", symbol: "psi", pascals: 4.448_221_615_260_5 / 0.000_645_16 },
];

export function findPressureUnit(id: string): PressureUnit {
  return PRESSURE_UNITS.find((unit) => unit.id === id) ?? PRESSURE_UNITS[0];
}

export function pressureToPascals(value: number, unit: string): number {
  return value * findPressureUnit(unit).pascals;
}

export function pressureFromPascals(pascals: number, unit: string): number {
  return pascals / findPressureUnit(unit).pascals;
}

export type TemperatureUnit = "K" | "C" | "F";

export const TEMPERATURE_UNITS: readonly {
  id: TemperatureUnit;
  symbol: string;
}[] = [
  { id: "K", symbol: "K" },
  { id: "C", symbol: "°C" },
  { id: "F", symbol: "°F" },
];

const FAHRENHEIT_OFFSET = 459.67;
const FAHRENHEIT_RATIO = 5 / 9;

export function toKelvin(value: number, unit: string): number {
  if (unit === "C") {
    return value + CELSIUS_OFFSET;
  }
  if (unit === "F") {
    return (value + FAHRENHEIT_OFFSET) * FAHRENHEIT_RATIO;
  }
  return value;
}

export function fromKelvin(kelvin: number, unit: string): number {
  if (unit === "C") {
    return kelvin - CELSIUS_OFFSET;
  }
  if (unit === "F") {
    return kelvin / FAHRENHEIT_RATIO - FAHRENHEIT_OFFSET;
  }
  return kelvin;
}

/** Size of one degree on the temperature scale, in kelvin (for temperature *differences*). */
export function degreeSize(unit: string): number {
  return unit === "F" ? FAHRENHEIT_RATIO : 1;
}

export function temperatureSymbol(id: string): string {
  return TEMPERATURE_UNITS.find((unit) => unit.id === id)?.symbol ?? "K";
}
